// InterviewPrep.js
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getFirestore, doc, setDoc, onSnapshot, getDoc, serverTimestamp, collection, addDoc, query, orderBy, deleteDoc, updateDoc } from 'firebase/firestore';
import { Plus, XCircle, Video, Mic, MicOff, ArrowRight, Lock, Star, Trash2 } from 'lucide-react';
import { db } from './firebase';
import { apiCall } from './utils/api';

import avatar1 from './img/WhatsApp Image 2025-08-25 at 7.22.51 PM.jpeg';
import avatar2 from './img/WhatsApp Image 2025-09-05 at 5.40.30 PM (1).jpeg';
import avatar3 from './img/WhatsApp Image 2025-09-05 at 5.40.30 PM (2).jpeg';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/* ------------ UI helpers ------------ */
const Input = ({ label, ...props }) => (
  <div className="space-y-2 w-full">
    {label && <label className="text-sm font-medium text-gray-400">{label}</label>}
    <input {...props} className="w-full px-4 py-3 bg-gray-800 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-[#FFD600] focus:border-[#FFD600] transition-shadow" />
  </div>
);
const TextArea = ({ label, ...props }) => (
  <div className="space-y-2">
    <label className="text-sm font-medium text-gray-400">{label}</label>
    <textarea {...props} rows="4" className="w-full px-4 py-3 bg-gray-800 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-[#FFD600] focus:border-[#FFD600] transition-shadow"></textarea>
  </div>
);

/* ------------ resume -> text ------------ */
const formatResumeDataAsText = (data) => {
  if (!data || !data.personalInfo) return "";
  let text = `Name: ${data.personalInfo.name}\nEmail: ${data.personalInfo.email}\nPhone: ${data.personalInfo.phone}\nLocation: ${data.personalInfo.location}\n\n`;
  if (data.goal) text += `## Goal\n${data.goal}\n\n`;
  text += "## Work Experience\n";
  (data.experience || []).forEach(exp => {
    text += `- ${exp.title} at ${exp.company} (${exp.period})\n  ${exp.description}\n`;
  });
  text += "\n## Education\n";
  (data.education || []).forEach(edu => {
    text += `- ${edu.degree} from ${edu.school} (${edu.period})\n  ${edu.description}\n`;
  });
  text += "\n## Projects\n";
  (data.projects || []).forEach(proj => {
    text += `- ${proj.title} (${proj.period})\n  ${proj.description}\n`;
  });
  text += "\n## Skills\n";
  {
    const skills = Array.isArray(data.skills)
      ? data.skills.filter(Boolean).map(String)
      : (typeof data.skills === 'string'
        ? data.skills.split(',').map(s => s.trim()).filter(Boolean)
        : []);
    text += skills.join(', ');
  }
  text += "\n\n## Achievements\n";
  (data.achievements || []).forEach(ach => { text += `- ${ach.description}\n`; });
  text += "\n## Awards\n";
  (data.awards || []).forEach(award => { text += `- ${award.description}\n`; });
  text += "\n## Certificates\n";
  (data.certificates || []).forEach(cert => { text += `- ${cert.description}\n`; });
  text += "\n## Hobbies\n";
  (data.hobbies || []).forEach(hobby => { text += `- ${hobby.description}\n`; });
  return text;
};

const InterviewPrep = ({ user, userData, resumeData, appId }) => {
  /* ------------ All hooks must be declared first (Rules of Hooks) ------------ */
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [sessionHistory, setSessionHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [isStartModalOpen, setIsStartModalOpen] = useState(false);
  const [viewingSession, setViewingSession] = useState(null);
  const [sessionState, setSessionState] = useState('idle'); // idle | loading_questions | in_session | evaluating | feedback
  const [error, setError] = useState(null);
  const [activeSessionData, setActiveSessionData] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showNextButton, setShowNextButton] = useState(false); // Fixed: Start with false so record button is enabled
  const [timer, setTimer] = useState(120); // 2 minute for interview recording
  const [wsConnectionStatus, setWsConnectionStatus] = useState('disconnected'); // Track WebSocket connection status
  const [interviewTab, setInterviewTab] = useState('qna');
  const [qnaData, setQnaData] = useState([]);
  const [isLoadingQnA, setIsLoadingQnA] = useState(false);
  const [serverFinalTranscript, setServerFinalTranscript] = useState(""); // from WS (ElevenLabs)
  const [liveTranscript, setLiveTranscript] = useState("");               // local interim (Web Speech API)
  const [simpleSessionId, setSimpleSessionId] = useState(null);
  const [simpleQuestions, setSimpleQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [simpleAnswered, setSimpleAnswered] = useState({});
  const [isUploadingAnswer, setIsUploadingAnswer] = useState(false);
  const [isCountingDown, setIsCountingDown] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [firstCountdownDone, setFirstCountdownDone] = useState(false);
  const [hasRecording, setHasRecording] = useState(false);
  const [currentBlob, setCurrentBlob] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(30); // 30 seconds for basic interview
  const [sttSupported] = useState(
    typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition)
      ? true
      : false
  );

  const timerRef = useRef(null);
  const socketRef = useRef(null);
  const audioStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const selectedMimeTypeRef = useRef('');
  const vadStateRef = useRef({ audioContext: null, analyser: null, source: null, silenceTimer: null, rafId: null });
  const processingTimeoutRef = useRef(null); // Use ref instead of state to avoid re-renders
  const wsConnectionTimeoutRef = useRef(null); // Track WebSocket connection timeout
  const firstQuestionTimeoutRef = useRef(null); // Track timeout for first question
  const simpleTimerIntervalRef = useRef(null);
  const chunkSeqRef = useRef(0);
  const speechRecRef = useRef(null);

  /* ------------ Constants ------------ */
  const CHUNK_MS = 1000;
  const SILENCE_THRESHOLD = 0.01;
  const SILENCE_DURATION_MS = 800;

  /* ------------ useCallback hooks (before premium check) ------------ */
  const flushBufferedUtterance = useCallback(() => {
    if (!audioChunksRef.current || audioChunksRef.current.length === 0) return;
    const blobType = selectedMimeTypeRef.current || undefined;
    const utteranceBlob = new Blob(audioChunksRef.current, blobType ? { type: blobType } : undefined);
    try {
      const ws = socketRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        console.log(`[ADVANCED] Sending audio chunk - size: ${utteranceBlob.size}, type: ${utteranceBlob.type}`);
        ws.send(utteranceBlob);
      } else {
        console.warn(`[ADVANCED] WebSocket not open, readyState: ${ws?.readyState}, cannot send audio`);
      }
    } catch (err) {
      console.error("[ADVANCED] Error sending audio chunk:", err);
    }
    audioChunksRef.current = [];
  }, []);

  const endInterview = useCallback(async (completedByAgent = false) => {
    if (isRecording && mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      flushBufferedUtterance(); // Send any remaining buffered audio
      stopLocalSTT();
      try {
        clearTimeout(vadStateRef.current.silenceTimer);
        if (vadStateRef.current.rafId) cancelAnimationFrame(vadStateRef.current.rafId);
        vadStateRef.current.analyser?.disconnect();
        vadStateRef.current.source?.disconnect();
        await vadStateRef.current.audioContext?.close();
      } catch { }
      vadStateRef.current = { audioContext: null, analyser: null, source: null, silenceTimer: null, rafId: null };
    }
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN && !completedByAgent) {
      socketRef.current.close();
    }
    setSessionState('evaluating');
    try {
      const { data } = await apiCall(`/api/evaluate-interview/${activeSessionData.sessionId}`, { method: 'POST' });

      const sessionDocRef = doc(db, `artifacts/${appId}/users/${user.uid}/interview_sessions`, activeSessionData.firestoreId);
      await updateDoc(sessionDocRef, { evaluation: data.evaluation, transcript: data.transcript });

      setActiveSessionData(prev => ({ ...prev, evaluationResult: data }));
      setSessionState('feedback');
    } catch (err) {
      setError(err.message);
      setSessionState('idle');
    }
  }, [user, activeSessionData, isRecording, db, appId, flushBufferedUtterance]);

  // Declare stopLocalSTT before it's used in endInterview
  const stopLocalSTT = () => {
    try {
      if (speechRecRef.current) {
        speechRecRef.current.onend = null;
        speechRecRef.current.stop();
        speechRecRef.current = null;
      }
    } catch { }
  };

  /* ------------ useEffect hooks (before premium check) ------------ */
  /* ------------ history subscription ------------ */
  useEffect(() => {
    //if (user && userData?.role === 'premium') { //uncommented to allow all users to see history for premium feature trials
     if (user) {
      const q = query(collection(db, `artifacts/${appId}/users/${user.uid}/interview_sessions`), orderBy("createdAt", "desc"));
      const unsubscribe = onSnapshot(q, (qs) => {
        const history = [];
        qs.forEach((d) => history.push({ id: d.id, ...d.data() }));
        setSessionHistory(history);
        setIsLoadingHistory(false);
      }, () => {
        setError("Could not fetch past interviews.");
        setIsLoadingHistory(false);
      });
      return () => unsubscribe();
    }
  }, [user, db, appId, userData?.role]);

  /* ------------ Advanced WS (listen for server final transcripts) ------------ */
  useEffect(() => {
    if (userData?.role === 'premium' && sessionState === 'in_session' && activeSessionData?.sessionId && activeSessionData?.wsUrl && !socketRef.current && user) {
      // Get Firebase auth token and append to WebSocket URL
      const connectWebSocket = async () => {
        try {
          const token = await user.getIdToken();
          if (!token) {
            setError("Authentication failed. Please sign in again.");
            setSessionState('idle');
            return;
          }

          // Set connection timeout
          wsConnectionTimeoutRef.current = setTimeout(() => {
            console.error("[ADVANCED] WebSocket connection timeout after 15 seconds");
            setError("Connection timeout. Please check your internet and try again.");
            setWsConnectionStatus('failed');
            if (socketRef.current) {
              socketRef.current.close();
              socketRef.current = null;
            }
          }, 15000); // 15 second timeout

          // Append token as query parameter to WebSocket URL
          let wsUrl;
          try {
            // If wsUrl is already a full URL, use it directly
            if (activeSessionData.wsUrl.startsWith('ws://') || activeSessionData.wsUrl.startsWith('wss://')) {
              wsUrl = new URL(activeSessionData.wsUrl);
            } else {
              // If it's a relative path, construct full URL
              const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
              const host = window.location.host;
              wsUrl = new URL(activeSessionData.wsUrl, `${protocol}//${host}`);
            }
            wsUrl.searchParams.set('token', token);
          } catch (urlError) {
            console.error("Invalid WebSocket URL:", activeSessionData.wsUrl, urlError);
            setError("Invalid WebSocket URL configuration. Please try again.");
            setSessionState('idle');
            if (wsConnectionTimeoutRef.current) {
              clearTimeout(wsConnectionTimeoutRef.current);
              wsConnectionTimeoutRef.current = null;
            }
            return;
          }

          console.log("[ADVANCED] Connecting to WebSocket...");
          setWsConnectionStatus('connecting');

          const ws = new WebSocket(wsUrl.toString());
          socketRef.current = ws;

          ws.onopen = () => {
            console.log("[ADVANCED] WebSocket connected successfully");
            console.log("[ADVANCED] Session ID:", activeSessionData.sessionId);
            console.log("[ADVANCED] WebSocket URL:", wsUrl.toString().replace(/token=.*/, 'token=REDACTED'));
            console.log("[ADVANCED] WebSocket readyState:", ws.readyState);

            // Clear connection timeout
            if (wsConnectionTimeoutRef.current) {
              clearTimeout(wsConnectionTimeoutRef.current);
              wsConnectionTimeoutRef.current = null;
            }

            setWsConnectionStatus('connected');
            setError(null);

            // Set a 30-second timeout for receiving the first question
            firstQuestionTimeoutRef.current = setTimeout(() => {
              console.error("[ADVANCED] First question timeout - no question received after 30 seconds");
              setError("No question received from server. Please try ending and restarting the session.");
              setActiveSessionData(prev => ({
                ...prev,
                interviewerQuestion: 'No question received. Please try restarting the session.'
              }));
            }, 30000);

            // Log that we're waiting for the first question
            console.log("[ADVANCED] Waiting for first question from server...");
          };

          ws.onclose = (event) => {
            console.log("[ADVANCED] WebSocket disconnected", { code: event.code, reason: event.reason });
            socketRef.current = null;
            setWsConnectionStatus('disconnected');

            // Clear connection timeout if still pending
            if (wsConnectionTimeoutRef.current) {
              clearTimeout(wsConnectionTimeoutRef.current);
              wsConnectionTimeoutRef.current = null;
            }

            // If closed unexpectedly during session, show error
            if (sessionState === 'in_session' && event.code !== 1000) {
              setError("Connection lost. Please try starting a new session.");
            }
          };

          ws.onerror = (error) => {
            console.error("[ADVANCED] WebSocket error:", error);
            setWsConnectionStatus('failed');

            // Clear connection timeout
            if (wsConnectionTimeoutRef.current) {
              clearTimeout(wsConnectionTimeoutRef.current);
              wsConnectionTimeoutRef.current = null;
            }

            setError("Connection error. Please check your internet and try again.");
          };

          ws.onmessage = (event) => {
            const timestamp = new Date().toISOString();
            console.log(`[ADVANCED] [${timestamp}] WebSocket message received:`, event.data);
            console.log(`[ADVANCED] Message type: ${typeof event.data}, length: ${event.data?.length || 0}`);

            let message;
            try {
              if (typeof event.data === 'string') {
                message = JSON.parse(event.data);
              } else if (event.data instanceof Blob) {
                console.warn("[ADVANCED] Received Blob data, expected JSON. Ignoring.");
                return;
              } else {
                message = event.data;
              }
            } catch (err) {
              console.error("[ADVANCED] Failed to parse WebSocket message:", err, "Raw data:", event.data);
              return;
            }

            const type = message.type || message.msgType || message.event || 'unknown';
            const payload = message.data ?? message.text ?? message.payload ?? null;

            console.log(`[ADVANCED] Parsed message - type: "${type}", payload type: ${typeof payload}, payload length: ${payload?.length || 0}`);

            switch (type) {
              case 'new_question':
                console.log("[ADVANCED] NEW QUESTION received:", payload?.substring(0, 100));
                // Clear first question timeout since we received a question
                if (firstQuestionTimeoutRef.current) {
                  clearTimeout(firstQuestionTimeoutRef.current);
                  firstQuestionTimeoutRef.current = null;
                }
                setActiveSessionData(prev => ({ ...prev, interviewerQuestion: payload || '', questionFeedback: '' }));
                setServerFinalTranscript("");
                setLiveTranscript("");
                setShowNextButton(false);
                setIsProcessing(false);
                setTimer(120); // 2 minute for interview recording
                // Clear any pending processing timeout using ref
                if (processingTimeoutRef.current) {
                  clearTimeout(processingTimeoutRef.current);
                  processingTimeoutRef.current = null;
                }
                break;
              case 'partial_transcript':
                // Real-time partial transcript from ElevenLabs Realtime (as you speak)
                console.log("[ADVANCED] PARTIAL TRANSCRIPT (realtime):", payload?.substring(0, 100));
                // Combine with committed transcripts for display
                setServerFinalTranscript(prev => {
                  // Keep committed text, append partial
                  const committed = prev.split('|PARTIAL|')[0]; // Split on marker if exists
                  return committed ? `${committed} ${payload || ''}` : (payload || '');
                });
                break;
              case 'committed_transcript':
                // Committed transcript from ElevenLabs Realtime (confirmed text)
                console.log("[ADVANCED] COMMITTED TRANSCRIPT (realtime):", payload?.substring(0, 100));
                setServerFinalTranscript(prev => {
                  // Append committed text, clear any partial
                  const committed = prev.split('|PARTIAL|')[0];
                  return committed ? `${committed} ${payload || ''}` : (payload || '');
                });
                break;
              case 'final_transcript':
                // Batch transcript (fallback mode)
                console.log("[ADVANCED] TRANSCRIPT received:", payload?.substring(0, 100));
                setServerFinalTranscript(payload || '');
                break;
              case 'question_feedback':
                console.log("[ADVANCED] FEEDBACK received:", payload?.substring(0, 100));
                // Clear processing timeout since we got a response using ref
                if (processingTimeoutRef.current) {
                  clearTimeout(processingTimeoutRef.current);
                  processingTimeoutRef.current = null;
                }
                setIsProcessing(false);
                setActiveSessionData(prev => ({ ...prev, questionFeedback: payload || '' }));
                setShowNextButton(true);
                break;
              case 'interview_complete':
                console.log("[ADVANCED] INTERVIEW COMPLETE");
                endInterview(true);
                break;
              case 'error':
                console.error("[ADVANCED] ERROR from backend:", payload);
                // Clear processing timeout on error using ref
                if (processingTimeoutRef.current) {
                  clearTimeout(processingTimeoutRef.current);
                  processingTimeoutRef.current = null;
                }
                setError(payload || 'Unknown error from server.');
                setIsProcessing(false);
                break;
              default:
                console.warn("[ADVANCED] Unknown message type:", type, "Full message:", message);
                break;
            }
          };
        } catch (err) {
          console.error("Error connecting WebSocket:", err);
          setError("Failed to connect to interview session. Please try again.");
          setWsConnectionStatus('failed');

          // Clear connection timeout
          if (wsConnectionTimeoutRef.current) {
            clearTimeout(wsConnectionTimeoutRef.current);
            wsConnectionTimeoutRef.current = null;
          }
        }
      };

      connectWebSocket();
    }

    return () => {
      // Clean up connection timeout
      if (wsConnectionTimeoutRef.current) {
        clearTimeout(wsConnectionTimeoutRef.current);
        wsConnectionTimeoutRef.current = null;
      }

      // Clean up first question timeout
      if (firstQuestionTimeoutRef.current) {
        clearTimeout(firstQuestionTimeoutRef.current);
        firstQuestionTimeoutRef.current = null;
      }

      // Clean up processing timeout using ref
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
        processingTimeoutRef.current = null;
      }

      // Clean up WebSocket
      if (socketRef.current && sessionState !== 'in_session') {
        socketRef.current.close();
        socketRef.current = null;
      }

      // Clean up audio stream
      if (sessionState !== 'in_session' && audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => {
          try {
            track.stop();
          } catch { }
        });
        audioStreamRef.current = null;
      }

      // Clean up MediaRecorder
      if (mediaRecorderRef.current && sessionState !== 'in_session') {
        if (mediaRecorderRef.current.state === 'recording') {
          try {
            mediaRecorderRef.current.stop();
          } catch { }
        }
        mediaRecorderRef.current = null;
      }

      // Clean up VAD (Voice Activity Detection)
      if (vadStateRef.current.silenceTimer) {
        clearTimeout(vadStateRef.current.silenceTimer);
      }
      if (vadStateRef.current.rafId) {
        cancelAnimationFrame(vadStateRef.current.rafId);
      }
      if (vadStateRef.current.analyser) {
        try { vadStateRef.current.analyser.disconnect(); } catch { }
      }
      if (vadStateRef.current.source) {
        try { vadStateRef.current.source.disconnect(); } catch { }
      }
      if (vadStateRef.current.audioContext && sessionState !== 'in_session') {
        try { vadStateRef.current.audioContext.close(); } catch { }
      }
      vadStateRef.current = { audioContext: null, analyser: null, source: null, silenceTimer: null, rafId: null };

      // Clean up speech recognition
      if (speechRecRef.current) {
        try {
          speechRecRef.current.stop();
        } catch { }
        speechRecRef.current = null;
      }
    };
  }, [sessionState, activeSessionData?.wsUrl, activeSessionData?.sessionId, userData?.role, user, endInterview]);

  /* ------------ timer for Advanced preview ------------ */
  useEffect(() => {
    if (userData?.role === 'premium' && isRecording && timer > 0) {
      timerRef.current = setInterval(() => setTimer(prev => prev - 1), 1000);
    } else if (timer === 0 && isRecording) {
      if (mediaRecorderRef.current) mediaRecorderRef.current.stop();
    }
    return () => clearInterval(timerRef.current);
  }, [isRecording, timer, userData?.role]);

  /* ------------ Premium check after hooks ------------ */
  // if (!userData || userData.role !== 'premium') {
  //   return (
  //     <div>
  //       <header className="flex justify-between items-center mb-8">
  //         <h2 className="text-3xl font-bold text-black-500 flex items-center gap-3">
  //           <img src={avatar3} alt="kris" className="w-10 h-10 rounded-full" />
  //           <span className="text-black-500">AI Interview Simulation</span>
  //         </h2>
  //       </header>
  //       <div className="flex flex-col items-center justify-center min-h-[400px] bg-[#fafafa] rounded-xl border border-[#d5e7f3] p-8">
  //         <Lock size={64} className="text-[#3C0F6C] mb-4" />
  //         <h3 className="text-2xl font-bold text-[#3C0F6C] mb-2">Premium Feature</h3>
  //         <p className="text-[#3C0F6C]/80 mb-6 text-center max-w-md">
  //           This feature is available for premium users only. Upgrade your account to access AI Interview Simulation.
  //         </p>
  //         <button
  //           onClick={() => setIsUpgradeModalOpen(true)}
  //           className="bg-[#3C0F6C] text-[#fafafa] font-semibold py-3 px-6 rounded-xl hover:bg-[#3C0F6C]/90 transition-colors"
  //         >
  //           Upgrade to Premium
  //         </button>
  //       </div>
  //       <UpgradeModal isOpen={isUpgradeModalOpen} onClose={() => setIsUpgradeModalOpen(false)} />
  //     </div>
  //   );
  // }

  const startLocalSTT = () => {
    if (!sttSupported) return;
    try {
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      const rec = new SR();
      rec.lang = "en-US";
      rec.interimResults = true;
      rec.continuous = true;

      rec.onresult = (e) => {
        // Build complete transcript from all results (both final and interim)
        // This ensures we show everything spoken continuously
        let fullTranscript = "";
        for (let i = 0; i < e.results.length; i++) {
          const transcript = e.results[i][0]?.transcript || "";
          if (transcript) {
            fullTranscript += transcript + " ";
          }
        }

        // Update state with the complete transcript (all final segments + current interim)
        if (fullTranscript.trim()) {
          setLiveTranscript(fullTranscript.trim());
        }
      };
      rec.onerror = (err) => console.warn("SpeechRecognition error:", err);
      rec.onend = () => {
        if (isRecording) {
          try { rec.start(); } catch { }
        }
      };
      rec.start();
      speechRecRef.current = rec;
    } catch (e) {
      console.warn("Unable to start local STT:", e);
    }
  };


  /* ------------ start interview ------------ */
  const startInterview = async ({ mode, basicInfo, jobDescriptionText, jobDescriptionFile, jdInputType }) => {
    const canStart = (mode === 'basic' && basicInfo.jobTitle) ||
      (mode === 'advanced' && ((jdInputType === 'text' && jobDescriptionText) || (jdInputType === 'file' && jobDescriptionFile)));
    if (!canStart) return;

    setSessionState('loading_questions');
    setIsStartModalOpen(false);
    setError(null);
    setIsLoadingQnA(true);
    setServerFinalTranscript("");
    setLiveTranscript("");

    const formData = new FormData();
    let jobTitleForHistory = "Interview Session";
    let jdTextForQnA = "";

    if (mode === 'basic') {
      try {
        const { data } = await apiCall('/api/interview/start', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ job_title: basicInfo.jobTitle, seniority: basicInfo.seniority })
        });
        const qs = Array.isArray(data.questions) ? data.questions : [];
        setSimpleQuestions(qs);
        setSimpleSessionId(data.session_id);
        setCurrentQuestionIndex(0);
        setInterviewTab('simulation');
        setSessionState('in_session');
        setIsLoadingQnA(false);

        // Generate Q&A with example answers for simple interview
        let qnaList = [];
        try {
          const qnaFormData = new FormData();
          qnaFormData.append('job_description_text', `Job Title: ${basicInfo.jobTitle}\nSeniority Level: ${basicInfo.seniority}`);
          qnaFormData.append('resume_text', formatResumeDataAsText(resumeData));
          const { data: qnaResult } = await apiCall('/api/generate-qna', { method: 'POST', body: qnaFormData });
          qnaList = Array.isArray(qnaResult?.qna_list) ? qnaResult.qna_list : [];
        } catch (qnaErr) {
          console.warn('Failed to generate Q&A for simple interview, using questions only:', qnaErr);
          // Fallback: use questions without answers
          qnaList = qs.map(q => ({ question: q, answer: '' }));
        }

        const interviewSessionsCol = collection(db, `artifacts/${appId}/users/${user.uid}/interview_sessions`);
        const newSessionRef = await addDoc(interviewSessionsCol, {
          title: basicInfo.jobTitle,
          qna_list: qnaList.length > 0 ? qnaList : qs.map(q => ({ question: q, answer: '' })),
          createdAt: serverTimestamp(),
          evaluation: null,
          transcript: null,
        });

        // Store Firestore document ID and session ID for later updates
        setActiveSessionData({
          sessionId: data.session_id,
          firestoreId: newSessionRef.id,
          evaluationResult: null
        });
        return;
      } catch (err) {
        setError(err.message || 'Failed to start interview.');
        setSessionState('idle');
        setIsLoadingQnA(false);
        return;
      }
    } else {
      if (jdInputType === 'text') {
        jdTextForQnA = jobDescriptionText;
        formData.append('job_description_text', jobDescriptionText);
        jobTitleForHistory = jobDescriptionText.split('\n')[0].substring(0, 50) || "Interview Session";
      } else {
        const fileContent = await jobDescriptionFile.text();
        jdTextForQnA = fileContent;
        formData.append('job_description_file', jobDescriptionFile);
        jobTitleForHistory = jobDescriptionFile.name;
      }
      formData.append('resume_text', formatResumeDataAsText(resumeData));
    }
    formData.append('seniority', basicInfo.seniority);

    let newSessionRef;
    try {
      const qnaFormData = new FormData();
      qnaFormData.append('job_description_text', jdTextForQnA);
      qnaFormData.append('resume_text', formatResumeDataAsText(resumeData));
      const { data: qnaResult } = await apiCall('/api/generate-qna', { method: 'POST', body: qnaFormData });
      setQnaData(qnaResult.qna_list);
      setIsLoadingQnA(false);

      const interviewSessionsCol = collection(db, `artifacts/${appId}/users/${user.uid}/interview_sessions`);
      newSessionRef = await addDoc(interviewSessionsCol, {
        title: jobTitleForHistory,
        qna_list: qnaResult.qna_list,
        createdAt: serverTimestamp(),
        evaluation: null,
        transcript: null,
      });

      const { data } = await apiCall('/api/start-interview-session', { method: 'POST', body: formData });

      const buildWebSocketUrl = (raw) => {
        try {
          const browserHost = window.location.hostname;
          const isHttps = window.location.protocol === 'https:';
          const desiredProtocol = isHttps ? 'wss:' : 'ws:';
          // Try REACT_APP_BACKEND_HOST first, then extract from API_BASE_URL, then fallback to browser hostname
          let envHost = process.env.REACT_APP_BACKEND_HOST;
          if (!envHost && API_BASE_URL) {
            try {
              const apiUrl = new URL(API_BASE_URL);
              envHost = apiUrl.hostname;
            } catch {
              envHost = browserHost;
            }
          }
          if (!envHost) {
            envHost = browserHost;
          }
          const envPort = process.env.REACT_APP_BACKEND_WS_PORT;

          if (typeof raw === 'string' && (raw.startsWith('ws://') || raw.startsWith('wss://'))) {
            const u = new URL(raw);
            const port = envPort || u.port || (isHttps ? '' : '8000');
            const hostWithPort = port ? `${envHost}:${port}` : envHost;
            return `${desiredProtocol}//${hostWithPort}${u.pathname}${u.search}`;
          }
          const port = envPort || (isHttps ? '' : '8000');
          const hostWithPort = port ? `${envHost}:${port}` : envHost;
          const path = (typeof raw === 'string' && raw.startsWith('/')) ? raw : `/ws/${data.session_id}`;
          return `${desiredProtocol}//${hostWithPort}${path}`;
        } catch {
          const isHttps = window.location.protocol === 'https:';
          const desiredProtocol = isHttps ? 'wss:' : 'ws:';
          // Extract host from API_BASE_URL or use env var, fallback to browser hostname
          let fallbackHost = process.env.REACT_APP_BACKEND_HOST;
          if (!fallbackHost && API_BASE_URL) {
            try {
              const apiUrl = new URL(API_BASE_URL);
              fallbackHost = apiUrl.hostname;
            } catch {
              fallbackHost = window.location.hostname;
            }
          }
          if (!fallbackHost) {
            fallbackHost = window.location.hostname;
          }
          const port = process.env.REACT_APP_BACKEND_WS_PORT || (isHttps ? '' : '8000');
          const hostWithPort = port ? `${fallbackHost}:${port}` : fallbackHost;
          const path = `/ws/${data.session_id}`;
          return `${desiredProtocol}//${hostWithPort}${path}`;
        }
      };

      const normalizedWsUrl = buildWebSocketUrl(data.ws_url);

      console.log("[ADVANCED] Starting advanced interview session");
      console.log("[ADVANCED] Session ID:", data.session_id);
      console.log("[ADVANCED] WebSocket URL:", normalizedWsUrl);

      setActiveSessionData({
        sessionId: data.session_id,
        wsUrl: normalizedWsUrl,
        firestoreId: newSessionRef.id,
        jobTitle: jobTitleForHistory,
        interviewerQuestion: 'Connecting to interview...',
        questionFeedback: '',
        evaluationResult: null,
      });
      setInterviewTab('simulation'); // Ensure advanced mode shows simulation view, not Q&A
      setSessionState('in_session');
      setServerFinalTranscript("");
      setLiveTranscript("");
      // Initialize button states properly for advanced interview
      setShowNextButton(false); // Allow recording to start
      setIsProcessing(false);    // Not processing yet
      setIsRecording(false);     // Not recording yet
    } catch (err) {
      setError(err.message);
      if (newSessionRef) {
        await deleteDoc(doc(db, `artifacts/${appId}/users/${user.uid}/interview_sessions`, newSessionRef.id));
      }
      setSessionState('idle');
      setIsLoadingQnA(false);
    }
  };

  /* ------------ chunk flush (Advanced) - using already declared flushBufferedUtterance ------------ */

  /* ------------ Advanced toggle recording (start/stop) ------------ */
  const handleToggleRecording = async () => {
    if (isRecording) {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      flushBufferedUtterance(); // Send any remaining buffered audio

      // Send stop_recording message to backend to process accumulated audio
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        try {
          socketRef.current.send("stop_recording");
          console.log("[ADVANCED] Sent stop_recording message to backend");
        } catch (err) {
          console.error("[ADVANCED] Error sending stop_recording message:", err);
        }
      }

      stopLocalSTT();
      try {
        clearTimeout(vadStateRef.current.silenceTimer);
        if (vadStateRef.current.rafId) cancelAnimationFrame(vadStateRef.current.rafId);
        vadStateRef.current.analyser?.disconnect();
        vadStateRef.current.source?.disconnect();
        await vadStateRef.current.audioContext?.close();
      } catch { }
      vadStateRef.current = { audioContext: null, analyser: null, source: null, silenceTimer: null, rafId: null };
      setIsRecording(false);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;

      const candidateMimeTypes = [
        'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/ogg', 'audio/mp4', 'audio/aac'
      ];
      const supportedMimeType = (typeof MediaRecorder !== 'undefined' && typeof MediaRecorder.isTypeSupported === 'function')
        ? candidateMimeTypes.find(type => MediaRecorder.isTypeSupported(type)) || ''
        : '';

      let mediaRecorder;
      try { mediaRecorder = supportedMimeType ? new MediaRecorder(stream, { mimeType: supportedMimeType }) : new MediaRecorder(stream); }
      catch { mediaRecorder = new MediaRecorder(stream); }

      selectedMimeTypeRef.current = supportedMimeType;
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      let chunkCount = 0; // Track chunks for periodic flushing

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
          chunkCount++;

          // Flush every 3 chunks (3 seconds) for live transcription
          if (chunkCount >= 3) {
            flushBufferedUtterance();
            chunkCount = 0;
          }
        }
      };
      mediaRecorder.onstop = () => {
        console.log("[ADVANCED] Recording stopped, processing audio...");
        console.log("[ADVANCED] Audio chunks collected:", audioChunksRef.current.length);
        setIsRecording(false);
        setIsProcessing(true);

        // Flush any remaining buffered audio chunks
        flushBufferedUtterance();

        // Send stop_recording message to backend to process accumulated audio
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
          try {
            socketRef.current.send("stop_recording");
            console.log("[ADVANCED] Sent stop_recording message to backend (from onstop)");
          } catch (err) {
            console.error("[ADVANCED] Error sending stop_recording message:", err);
          }
        }

        // Clean up VAD state
        try {
          clearTimeout(vadStateRef.current.silenceTimer);
          if (vadStateRef.current.rafId) cancelAnimationFrame(vadStateRef.current.rafId);
          vadStateRef.current.analyser?.disconnect();
          vadStateRef.current.source?.disconnect();
          vadStateRef.current.audioContext?.close();
        } catch { }
        vadStateRef.current = { audioContext: null, analyser: null, source: null, silenceTimer: null, rafId: null };

        // Set 60-second timeout for AI feedback
        const timeoutId = setTimeout(() => {
          console.error("[ADVANCED] Frontend timeout - no feedback after 60 seconds");
          setIsProcessing(false);
          setError("AI is taking too long. Please try again later.");
          processingTimeoutRef.current = null;
        }, 60000);
        processingTimeoutRef.current = timeoutId;

        stopLocalSTT();
        try { stream.getTracks().forEach(track => track.stop()); } catch { }
      };
      mediaRecorder.onerror = (err) => console.error('MediaRecorder error:', err);
      // Start with 1-second timeslice to ensure regular audio transmission
      mediaRecorder.start(1000);  // Send chunks every 1 second for live transcription

      // VAD
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new AudioContext();
      vadStateRef.current.audioContext = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      vadStateRef.current.source = source;
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 2048;
      vadStateRef.current.analyser = analyser;
      source.connect(analyser);

      const bufLen = analyser.fftSize;
      const dataArray = new Float32Array(bufLen);
      const computeRMS = () => {
        analyser.getFloatTimeDomainData(dataArray);
        let sum = 0; for (let i = 0; i < bufLen; i++) sum += dataArray[i] * dataArray[i];
        return Math.sqrt(sum / bufLen);
      };
      const scheduleSilenceFlush = () => {
        clearTimeout(vadStateRef.current.silenceTimer);
        vadStateRef.current.silenceTimer = setTimeout(() => {
          if (audioChunksRef.current.length > 0) flushBufferedUtterance();
        }, SILENCE_DURATION_MS);
      };
      const vadLoop = () => {
        const rms = computeRMS();
        if (rms > SILENCE_THRESHOLD) clearTimeout(vadStateRef.current.silenceTimer);
        else scheduleSilenceFlush();
        vadStateRef.current.rafId = requestAnimationFrame(vadLoop);
      };
      vadLoop();

      // Start local interim STT for on-screen "as you speak"
      setLiveTranscript("");
      startLocalSTT();

      setIsRecording(true);
      setTimer(120); // 2 minutes for interview recording
    } catch (err) {
      setError("Microphone access is required. Please allow it in your browser settings.");
    }
  };

  /* ------------ BASIC recording controls (with local STT) ------------ */
  const startSimpleRecording = async () => {
    if (!firstCountdownDone) {
      setIsCountingDown(true);
      setCountdown(5);
      const cd = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(cd);
            setIsCountingDown(false);
            setFirstCountdownDone(true);
            setTimeout(() => actuallyStartMediaRecorder(), 0);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return;
    }
    await actuallyStartMediaRecorder();
  };

  const playBeep = async (freq = 880, durationMs = 120) => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.type = 'sine'; osc.frequency.value = freq; gain.gain.value = 0.2;
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      setTimeout(() => { try { osc.stop(); ctx.close(); } catch { } }, durationMs);
    } catch { }
  };

  const actuallyStartMediaRecorder = async () => {
    // Clean up any existing recording first
    stopLocalSTT();
    if (simpleTimerIntervalRef.current) {
      clearInterval(simpleTimerIntervalRef.current);
      simpleTimerIntervalRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      try {
        mediaRecorderRef.current.stop();
      } catch { }
    }
    if (audioStreamRef.current) {
      try {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
      } catch { }
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;

      const candidateMimeTypes = [
        'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/ogg', 'audio/mp4', 'audio/aac'
      ];
      const supportedMimeType = (typeof MediaRecorder !== 'undefined' && typeof MediaRecorder.isTypeSupported === 'function')
        ? candidateMimeTypes.find(type => MediaRecorder.isTypeSupported(type)) || ''
        : '';

      let mediaRecorder;
      try { mediaRecorder = supportedMimeType ? new MediaRecorder(stream, { mimeType: supportedMimeType }) : new MediaRecorder(stream); }
      catch { mediaRecorder = new MediaRecorder(stream); }

      selectedMimeTypeRef.current = supportedMimeType;
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      chunkSeqRef.current = 0;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) audioChunksRef.current.push(event.data);
      };
      mediaRecorder.onstop = async () => {
        stopLocalSTT();
        try { stream.getTracks().forEach(t => t.stop()); } catch { }
        setIsRecording(false);

        const blobType = selectedMimeTypeRef.current || undefined;
        const finalBlob = new Blob(audioChunksRef.current, blobType ? { type: blobType } : undefined);
        setCurrentBlob(finalBlob);
        setHasRecording(true);

        if (simpleTimerIntervalRef.current) { clearInterval(simpleTimerIntervalRef.current); simpleTimerIntervalRef.current = null; }
        playBeep(660, 100);
      };
      try { mediaRecorder.start(1000); } catch { mediaRecorder.start(); }

      // Start local interim STT for "as you speak" in Basic
      setLiveTranscript("");
      startLocalSTT();

      setIsRecording(true);
      setTimeRemaining(30); // 30 seconds for basic interview
      playBeep(880, 100);
      if (simpleTimerIntervalRef.current) { clearInterval(simpleTimerIntervalRef.current); }
      simpleTimerIntervalRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            try { if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') mediaRecorderRef.current.stop(); } catch { }
            clearInterval(simpleTimerIntervalRef.current);
            simpleTimerIntervalRef.current = null;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch {
      setError('Microphone access is required.');
    }
  };

  const stopSimpleRecording = () => {
    stopLocalSTT();
    // Clear the timer interval first
    if (simpleTimerIntervalRef.current) {
      clearInterval(simpleTimerIntervalRef.current);
      simpleTimerIntervalRef.current = null;
    }
    // Stop the media recorder if it's recording
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    // Immediately set recording state to false (don't wait for onstop handler)
    setIsRecording(false);
    // Stop audio stream tracks
    if (audioStreamRef.current) {
      try {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
      } catch { }
    }
  };

  const rerecordSimpleAnswer = async () => {
    // Stop any active recording first
    stopLocalSTT();
    if (simpleTimerIntervalRef.current) {
      clearInterval(simpleTimerIntervalRef.current);
      simpleTimerIntervalRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    if (audioStreamRef.current) {
      try {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
      } catch { }
    }

    // If this question was already submitted, tell backend to reset that answer
    try {
      if (simpleSessionId && simpleAnswered[currentQuestionIndex]) {
        const fd = new FormData();
        fd.append('session_id', simpleSessionId);
        fd.append('question_index', String(currentQuestionIndex));
        fd.append('reset', 'true');
        await apiCall('/api/interview/answer', { method: 'POST', body: fd });
      }
    } catch (resetErr) {
      console.warn('Failed to reset answer on backend, will proceed locally:', resetErr);
    }

    // Reset all recording-related states locally
    setCurrentBlob(null);
    setHasRecording(false);
    setIsRecording(false);
    setIsCountingDown(false);
    setError(null);
    setLiveTranscript("");
    setServerFinalTranscript("");
    setTimeRemaining(60);
    chunkSeqRef.current = 0;
    // Clear the answered flag so user can submit the new recording
    setSimpleAnswered(prev => {
      const updated = { ...prev };
      delete updated[currentQuestionIndex];
      return updated;
    });
  };

  const uploadCurrentAnswer = async () => {
    // Idempotency guard - prevent double submission
    if (isUploadingAnswer) {
      console.log("[BASIC] Already uploading, ignoring duplicate request");
      return;
    }

    if (!currentBlob || simpleSessionId == null) return;
    console.log("[BASIC] Uploading answer - question:", currentQuestionIndex);
    setIsUploadingAnswer(true);
    setError(null);

    // Set 90-second timeout for answer upload + processing (allows for large files on slow connections)
    const timeoutId = setTimeout(() => {
      console.error("[BASIC] Frontend timeout - no response after 90 seconds");
      setIsUploadingAnswer(false);
      setError("Answer submission is taking too long. Please try again later.");
    }, 90000);

    try {
      const fd = new FormData();
      fd.append('session_id', simpleSessionId);
      fd.append('question_index', String(currentQuestionIndex));
      fd.append('audio_file', currentBlob, `answer_${currentQuestionIndex}.webm`);
      fd.append('is_last', 'true');
      // include local transcript (optional for backend)
      fd.append('transcript_text', (serverFinalTranscript || liveTranscript || ''));

      console.log("[BASIC] Sending answer to backend...");
      await apiCall('/api/interview/answer', { method: 'POST', body: fd });
      clearTimeout(timeoutId);
      console.log("[BASIC] Answer uploaded successfully");
      setSimpleAnswered(prev => ({ ...prev, [currentQuestionIndex]: true }));
    } catch (err) {
      clearTimeout(timeoutId);
      console.error("[BASIC] Failed to upload answer:", err);
      setError(err.message || 'Failed to submit answer.');
    } finally {
      setIsUploadingAnswer(false);
    }
  };

  const goToNextSimpleQuestion = () => {
    if (!simpleAnswered[currentQuestionIndex]) return;

    // Clean up any active recording before moving to next question
    stopLocalSTT();
    if (simpleTimerIntervalRef.current) {
      clearInterval(simpleTimerIntervalRef.current);
      simpleTimerIntervalRef.current = null;
    }
    if (mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      mediaRecorderRef.current = null;
    }
    if (audioStreamRef.current) {
      try {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
      } catch { }
      audioStreamRef.current = null;
    }

    // Reset all recording-related states for the new question
    setCurrentQuestionIndex(prev => prev + 1);
    setCurrentBlob(null);
    setHasRecording(false);
    setIsRecording(false);
    setIsCountingDown(false);
    setLiveTranscript("");
    setServerFinalTranscript("");
    setTimeRemaining(60);
    chunkSeqRef.current = 0;
  };

  const endSimpleSessionAndGetFeedback = async () => {
    if (!simpleSessionId) return;

    // Clean up any active recording before ending session
    stopLocalSTT();
    if (simpleTimerIntervalRef.current) {
      clearInterval(simpleTimerIntervalRef.current);
      simpleTimerIntervalRef.current = null;
    }
    if (mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      mediaRecorderRef.current = null;
    }
    if (audioStreamRef.current) {
      try {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
      } catch { }
      audioStreamRef.current = null;
    }
    console.log("[BASIC] Ending session and requesting feedback - session:", simpleSessionId);

    setSessionState('evaluating');

    // Set 60-second timeout for final feedback
    const timeoutId = setTimeout(() => {
      console.error("[BASIC] Frontend timeout - no feedback after 60 seconds");
      setSessionState('idle');
      setError("AI is taking too long. Please try again later.");
    }, 60000);

    try {
      console.log("[BASIC] Requesting feedback from backend...");
      const feedbackStartTime = Date.now();

      const { data } = await apiCall('/api/interview/feedback', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: simpleSessionId })
      });

      clearTimeout(timeoutId);
      const feedbackDuration = ((Date.now() - feedbackStartTime) / 1000).toFixed(2);
      console.log(`[BASIC] Feedback received - duration: ${feedbackDuration}s`);

      // Format feedback for display (handle both string and object formats)
      let evaluationText = '';
      if (typeof data.feedback === 'string') {
        evaluationText = data.feedback;
      } else if (data.feedback && typeof data.feedback === 'object') {
        // If feedback is an object with summary and breakdown
        const summary = data.feedback.summary || '';
        const breakdown = data.feedback.breakdown || {};
        const breakdownText = Object.entries(breakdown)
          .map(([key, value]) => {
            if (typeof value === 'object' && value.score !== undefined) {
              return `${key.charAt(0).toUpperCase() + key.slice(1)}: ${value.score}/100 - ${value.notes || ''}`;
            }
            return `${key}: ${JSON.stringify(value)}`;
          })
          .join('\n');
        evaluationText = summary + (breakdownText ? '\n\n' + breakdownText : '');
      } else {
        evaluationText = JSON.stringify(data.feedback, null, 2);
      }

      // Save evaluation to Firestore if we have the document ID (check before updating state)
      const firestoreIdToUpdate = activeSessionData?.firestoreId;

      setActiveSessionData(prev => ({ ...(prev || {}), evaluationResult: { evaluation: evaluationText } }));

      // Save evaluation to Firestore if we have the document ID
      if (firestoreIdToUpdate) {
        try {
          const sessionDocRef = doc(db, `artifacts/${appId}/users/${user.uid}/interview_sessions`, firestoreIdToUpdate);
          await updateDoc(sessionDocRef, {
            evaluation: evaluationText,
            transcript: data.transcripts ? JSON.stringify(data.transcripts) : null
          });
        } catch (firestoreErr) {
          console.error('Failed to save evaluation to Firestore:', firestoreErr);
          // Don't fail the whole flow if Firestore update fails
        }
      }

      setSessionState('feedback');
    } catch (err) {
      clearTimeout(timeoutId);
      console.error("[BASIC] Failed to get feedback:", err);
      setError(err.message || 'Failed to get feedback.');
      setSessionState('idle');
    }
  };

  /* ------------ end interview (Advanced) - using already declared endInterview ------------ */

  const handleNextQuestion = () => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      console.log("[ADVANCED] Sending 'next' message to request next question");
      socketRef.current.send("next");
    } else {
      console.error("[ADVANCED] Cannot send 'next' - WebSocket not open, readyState:", socketRef.current?.readyState);
      setError("Connection lost. Please refresh and try again.");
    }
  };

  const resetSession = () => {
    setSessionState('idle');
    setActiveSessionData(null);
    setQnaData([]);
    setError(null);
    setLiveTranscript("");
    setServerFinalTranscript("");
  };

  const handleDeleteSession = async (sessionId) => {
    if (window.confirm("Delete this session history?")) {
      const sessionDocRef = doc(db, `artifacts/${appId}/users/${user.uid}/interview_sessions`, sessionId);
      try { await deleteDoc(sessionDocRef); }
      catch { setError("Could not delete the session history."); }
    }
  };

  /* ------------ renders ------------ */
  if (sessionState === 'idle') {
    return (
      <div>
        <header className="flex justify-between items-center mb-8">
          <h2 className="text-3xl font-bold text-black-500 flex items-center gap-3">
            <img src={avatar3} alt="kris" className="w-10 h-10 rounded-full" />
            <span className="text-black-500">AI Interview Simulation</span>
          </h2>
          <button onClick={() => setIsStartModalOpen(true)} className="bg-[#FFD600] text-[#0E0E0E] font-semibold py-2 px-4 rounded-lg hover:bg-[#FFD600]/90 transition-colors flex items-center space-x-2">
            <Plus size={18} /><span>New Simulation</span>
          </button>
        </header>

        {isLoadingHistory ? <p>Loading history...</p> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            <CreateNewInterviewCard onClick={() => setIsStartModalOpen(true)} />
            {sessionHistory.map(session => (
              <InterviewHistoryCard key={session.id} session={session} onView={() => setViewingSession(session)} onDelete={() => handleDeleteSession(session.id)} />
            ))}
          </div>
        )}

        {isStartModalOpen && <StartInterviewModal isOpen={isStartModalOpen} onClose={() => setIsStartModalOpen(false)} onStart={startInterview} userData={userData} onUpgrade={() => setIsUpgradeModalOpen(true)} />}
        {viewingSession && <ViewSessionDetailModal session={viewingSession} onClose={() => setViewingSession(null)} />}
        <UpgradeModal isOpen={isUpgradeModalOpen} onClose={() => setIsUpgradeModalOpen(false)} />
      </div>
    );
  }

  if (sessionState === 'loading_questions') {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <svg className="animate-spin h-12 w-12 text-[#FFD600]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
        <h3 className="mt-4 text-xl font-semibold text-gray-300">Preparing Your Session...</h3>
        <p className="text-gray-500">Our AI is generating questions and setting up the simulation.</p>
      </div>
    );
  }

  if (sessionState === 'in_session' || sessionState === 'evaluating' || sessionState === 'feedback') {
    return (
      <div>
        <div className="mb-4 border-b border-gray-700">
          <nav className="-mb-px flex space-x-6">
            {!(simpleQuestions && simpleQuestions.length > 0) && (
              <button
                onClick={() => setInterviewTab('qna')}
                className={`py-3 px-1 border-b-2 font-semibold text-sm ${interviewTab === 'qna' ? 'border-[#FFD600] text-[#FFD600]' : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-gray-600'}`}
              >
                AI Q&A
              </button>
            )}
            <button onClick={() => setInterviewTab('simulation')} className={`py-3 px-1 border-b-2 font-semibold text-sm ${interviewTab === 'simulation' ? 'border-[#FFD600] text-[#FFD600]' : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-gray-600'}`}>AI Live Simulation</button>
          </nav>
        </div>

        {interviewTab === 'qna' ? (
          <AIQnAView qnaData={qnaData} isLoading={isLoadingQnA} jobTitle={activeSessionData?.jobTitle} />
        ) : (
          <div>
            {sessionState === 'evaluating' && <p>Evaluating your performance...</p>}
            {sessionState === 'feedback' && (
              <div className="bg-gray-900 p-8 rounded-xl border border-gray-700 shadow-sm">
                <h3 className="text-2xl font-bold mb-6 text-white">Interview Performance Evaluation</h3>
                <div className="prose prose-invert max-w-none whitespace-pre-wrap bg-gray-800 p-6 rounded-lg border border-gray-700">
                  {activeSessionData?.evaluationResult?.evaluation
                    ? (typeof activeSessionData.evaluationResult.evaluation === 'string'
                      ? activeSessionData.evaluationResult.evaluation.replace(/\*\*?|`{1,3}/g, '')
                      : JSON.stringify(activeSessionData.evaluationResult.evaluation, null, 2))
                    : "No evaluation available."
                  }
                </div>
                <button onClick={resetSession} className="mt-8 bg-[#FFD600] text-[#0E0E0E] font-semibold py-2 px-6 rounded-lg hover:bg-[#FFD600]/90 transition-colors">Finish Review & View History</button>
              </div>
            )}

            {sessionState === 'in_session' && (
              simpleQuestions && simpleQuestions.length > 0 ? (
                /* -------- BASIC UI with live transcript (local STT) -------- */
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2 bg-black rounded-xl flex flex-col justify-between text-white aspect-video p-6 border border-gray-700">
                    <div className="flex-grow flex items-center justify-center text-center">
                      <div className="space-y-2">
                        <div className="text-sm text-gray-300">Question {currentQuestionIndex + 1} of {simpleQuestions.length}</div>
                        <div className="text-xl font-semibold">{simpleQuestions[currentQuestionIndex]}</div>
                      </div>
                    </div>
                    <div className="w-full min-h-[10rem] max-h-64 bg-gray-900/50 rounded-lg p-3 text-lg leading-relaxed text-gray-200 overflow-y-auto">
                      <h3 className="text-sm font-semibold text-[#FFD600] mb-1">Your Answer (live)</h3>
                      <div className="whitespace-pre-wrap">
                        {isRecording && !liveTranscript && <span>Listening… start speaking.</span>}
                        {liveTranscript ? liveTranscript : (!isRecording && (hasRecording ? "Answer recorded. Submit when ready." : "Record your answer and submit to proceed."))}
                        {!sttSupported && isRecording && <div className="mt-1 text-xs text-gray-400">(Live transcript not supported in this browser)</div>}
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-900 p-6 rounded-xl border border-gray-700 shadow-sm flex flex-col">
                    <h3 className="text-lg font-bold mb-4 text-white">Controls</h3>
                    {isCountingDown && <div className="text-center text-yellow-400 font-semibold mb-3">Starting in {countdown}...</div>}
                    {isRecording && <div className="text-center text-gray-300 mb-2">Time remaining: {timeRemaining}s</div>}

                    <div className="space-y-2">
                      <button onClick={startSimpleRecording} disabled={isRecording || isCountingDown} className={`w-full font-semibold py-2.5 rounded-lg disabled:bg-gray-600 ${isRecording ? 'bg-red-600 text-white animate-pulse' : 'bg-[#FFD600] text-[#0E0E0E]'}`}>{isRecording ? 'Recording...' : 'Start Recording'}</button>
                      <button onClick={stopSimpleRecording} disabled={!isRecording} className="w-full bg-yellow-500 text-white font-semibold py-2.5 rounded-lg disabled:bg-gray-600">Stop Recording</button>
                      <button onClick={rerecordSimpleAnswer} disabled={isRecording || !hasRecording} className="w-full bg-gray-700 text-white font-semibold py-2.5 rounded-lg disabled:bg-gray-600">Re-record</button>
                      <button onClick={uploadCurrentAnswer} disabled={!hasRecording || isUploadingAnswer || simpleAnswered[currentQuestionIndex]} className="w-full bg-blue-500 text-white font-semibold py-2.5 rounded-lg disabled:bg-gray-600">Submit Answer</button>
                    </div>

                    <div className="mt-auto pt-4 space-y-2">
                      {currentQuestionIndex < simpleQuestions.length - 1 ? (
                        <button onClick={goToNextSimpleQuestion} disabled={!simpleAnswered[currentQuestionIndex]} className="w-full bg-[#FFD600] text-[#0E0E0E] font-semibold py-2.5 rounded-lg disabled:bg-gray-600">Next Question</button>
                      ) : (
                        <button onClick={endSimpleSessionAndGetFeedback} disabled={Object.keys(simpleAnswered).length !== simpleQuestions.length} className="w-full bg-red-600 text-white font-semibold py-2.5 rounded-lg disabled:bg-gray-600">End Session & Get Feedback</button>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                /* -------- ADVANCED live sim: show local live + server final -------- */
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2 bg-black rounded-xl flex flex-col justify-between text-white aspect-video p-6 border border-gray-700">
                    <div className="flex-grow flex items-center justify-center text-center">
                      <div>
                        <div className="text-6xl font-bold mb-4">{timer}</div>
                        {isRecording ? <Mic size={64} className="mx-auto text-[#FFD600] animate-pulse" /> : <MicOff size={64} className="mx-auto text-gray-400" />}
                        <p className="text-gray-300 mt-2">{isRecording ? "Listening..." : (isProcessing ? "Processing..." : "Mic is off")}</p>
                      </div>
                    </div>

                    <div className="w-full min-h-[8rem] max-h-56 bg-gray-900/50 rounded-lg p-3 text-lg leading-relaxed text-gray-200 overflow-y-auto">
                      <h3 className="text-sm font-semibold text-green-400 mb-1">Your Answer (live)</h3>
                      <p className="whitespace-pre-wrap">
                        {/* Show ElevenLabs Realtime transcript if available (streaming), otherwise fall back to local STT */}
                        {isRecording
                          ? (serverFinalTranscript || liveTranscript || "Listening… start speaking.")
                          : (serverFinalTranscript || liveTranscript || (isProcessing ? "Transcribing your answer..." : "Start recording to answer."))
                        }
                        {!sttSupported && isRecording && <span className="block mt-1 text-xs text-gray-400">(Live transcript not supported in this browser)</span>}
                      </p>
                    </div>
                  </div>

                  <div className="bg-gray-900 p-6 rounded-xl border border-gray-700 shadow-sm flex flex-col">
                    <h3 className="text-lg font-bold mb-4 text-white flex items-center gap-3">AI Interviewer <img src={avatar1} alt="interviewer" className="w-10 h-10 rounded-full" /></h3>
                    <div className="bg-gray-800 p-4 rounded-lg text-gray-300 flex-grow min-h-[6rem] flex items-center gap-4">
                      <div className="flex-shrink-0">
                        <img src={avatar2} alt="kris avatar" className="w-20 h-20 rounded-lg object-cover border-2 border-gray-700" />
                      </div>
                      <p className="flex-grow">
                        {wsConnectionStatus === 'connecting' && "Connecting to interview session..."}
                        {wsConnectionStatus === 'failed' && "Connection failed. Please refresh and try again."}
                        {wsConnectionStatus === 'connected' && (activeSessionData?.interviewerQuestion || "Connected. Waiting for first question...")}
                        {wsConnectionStatus === 'disconnected' && activeSessionData?.interviewerQuestion && activeSessionData.interviewerQuestion}
                      </p>
                    </div>

                    <div className="mt-4 w-full min-h-[6rem] max-h-56 bg-gray-800 rounded-lg p-3 text-sm leading-relaxed text-gray-200 overflow-y-auto border border-gray-700">
                      <h4 className="text-sm font-semibold text-blue-400 mb-1">Answer Feedback</h4>
                      <p className="whitespace-pre-wrap">
                        {activeSessionData?.questionFeedback || (isProcessing ? "Generating feedback..." : "Your feedback will appear here after you answer.")}
                      </p>
                    </div>

                    <div className="mt-auto pt-4 space-y-2">
                      <button onClick={handleToggleRecording} disabled={showNextButton || isProcessing} className={`w-full font-semibold py-2.5 rounded-lg transition-colors flex items-center justify-center space-x-2 ${isRecording ? 'bg-yellow-500 text-white hover:bg-yellow-600' : 'bg-[#FFD600] text-[#0E0E0E] hover:bg-[#FFD600]/90'} disabled:bg-gray-600 disabled:cursor-not-allowed`}>
                        {isRecording ? <MicOff size={18} /> : <Mic size={18} />}
                        <span>{isRecording ? 'Stop Recording' : 'Record Answer'}</span>
                      </button>
                      {showNextButton && (
                        <button onClick={handleNextQuestion} className="w-full bg-[#FFD600] text-[#0E0E0E] font-semibold py-2.5 rounded-lg hover:bg-[#FFD600]/90 transition-colors flex items-center justify-center space-x-2">
                          <span>Next Question</span>
                          <ArrowRight size={18} />
                        </button>
                      )}
                      <button onClick={() => endInterview(false)} className="w-full bg-red-600 text-white font-semibold py-2.5 rounded-lg hover:bg-red-700 transition-colors">End Session & Get Feedback</button>
                    </div>
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>
    );
  }
  return null;
};

/* ------------ subcomponents (unchanged) ------------ */
const AIQnAView = ({ qnaData, isLoading, jobTitle }) => {
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  useEffect(() => { if (qnaData && qnaData.length > 0) setSelectedQuestion(qnaData[0]); }, [qnaData]);
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <svg className="animate-spin h-8 w-8 text-[#FFD600]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a 8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
        <p className="ml-4 text-gray-400">Generating AI-powered Q&A...</p>
      </div>
    );
  }
  if (!qnaData || qnaData.length === 0) return <p className="text-center text-gray-500 py-10">No Q&A data is available for this session.</p>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      <div className="md:col-span-1 bg-gray-900 p-6 rounded-xl border border-gray-700 shadow-sm flex flex-col items-center">
        <img src={avatar2} alt="kris" className="w-20 h-20 rounded-full mb-4" />
        <h3 className="text-lg font-bold mb-4 text-white">{jobTitle}</h3>
        <nav className="space-y-2 w-full">
          {qnaData.map((item, index) => (
            <button
              key={index}
              onClick={() => setSelectedQuestion(item)}
              className={`w-full text-left p-3 rounded-lg text-sm font-medium transition-colors ${selectedQuestion?.question === item.question ? 'bg-gray-800 text-[#FFD600]' : 'text-gray-300 hover:bg-gray-800'}`}
            >
              {item.question}
            </button>
          ))}
        </nav>
      </div>
      <div className="md:col-span-2 bg-gray-900 p-6 rounded-xl border border-gray-700 shadow-sm">
        {selectedQuestion ? (
          <div>
            <h4 className="font-bold text-white mb-2">Question:</h4>
            <p className="mb-4 text-gray-300">{selectedQuestion.question}</p>
            <h4 className="font-bold text-white mb-2">Example Answer:</h4>
            <p className="text-gray-300 whitespace-pre-wrap">{selectedQuestion.answer}</p>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-500">Select a question to see the answer.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const StartInterviewModal = ({ isOpen, onClose, onStart, userData, onUpgrade }) => {
  const [mode, setMode] = useState('basic');
  const [jdInputType, setJdInputType] = useState('text');
  const [jobDescriptionText, setJobDescriptionText] = useState('');
  const [jobDescriptionFile, setJobDescriptionFile] = useState(null);
  const [basicInfo, setBasicInfo] = useState({ jobTitle: '', seniority: 'Mid' });
  const [isStarting, setIsStarting] = useState(false);

  const handleStartClick = async () => {
    setIsStarting(true);
    await onStart({ mode, basicInfo, jobDescriptionText, jobDescriptionFile, jdInputType });
    setIsStarting(false);
  };

  const handleModeChange = (newMode) => {
    if (newMode === 'advanced' && userData?.role !== 'premium') onUpgrade();
    else setMode(newMode);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50">
      <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-xl max-w-lg w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold text-white">New Interview Simulation</h3>
            <button onClick={onClose} className="p-1 rounded-full text-gray-400 hover:bg-gray-700"><XCircle size={20} /></button>
          </div>
          <div className="mb-4 border-b border-gray-700">
            <nav className="flex space-x-1 rounded-lg p-1 bg-gray-800">
              <button onClick={() => handleModeChange('basic')} className={`w-full py-2 text-sm font-semibold rounded-md transition-colors ${mode === 'basic' ? 'bg-gray-700 text-[#FFD600] shadow' : 'text-gray-400 hover:bg-gray-700/50'}`}>Basic</button>
              <button onClick={() => handleModeChange('advanced')} className={`relative w-full py-2 text-sm font-semibold rounded-md transition-colors ${mode === 'advanced' ? 'bg-gray-700 text-[#FFD600] shadow' : 'text-gray-400 hover:bg-gray-700/50'}`}>
                Advanced
                {userData?.role !== 'premium' && <Lock size={12} className="absolute top-1 right-2 text-yellow-400" />}
              </button>
            </nav>
          </div>

          {mode === 'basic' ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-400">Generates questions based on a job title and seniority level.</p>
              <Input label="Job title from the job ad (required)" value={basicInfo.jobTitle} onChange={e => setBasicInfo({ ...basicInfo, jobTitle: e.target.value })} maxLength={100} />
              <div>
                <label className="text-sm font-medium text-gray-400 block mb-2">Seniority</label>
                <div className="flex space-x-2">
                  {['Junior', 'Mid', 'Senior'].map(level => (
                    <button key={level} onClick={() => setBasicInfo({ ...basicInfo, seniority: level })} className={`flex-1 py-2 text-sm font-semibold rounded-lg border ${basicInfo.seniority === level ? 'bg-[#FFD600] text-[#0E0E0E] border-[#FFD600]' : 'bg-gray-800 text-gray-300 border-gray-600 hover:bg-gray-700'}`}>
                      {level}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-400">Combines your resume with a job description for highly personalized questions.</p>
              <div className="mb-4 border-b border-gray-700">
                <nav className="-mb-px flex space-x-6">
                  <button onClick={() => setJdInputType('text')} className={`py-3 px-1 border-b-2 font-semibold text-sm ${jdInputType === 'text' ? 'border-[#FFD600] text-[#FFD600]' : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-gray-600'}`}>Paste Text</button>
                  <button onClick={() => setJdInputType('file')} className={`py-3 px-1 border-b-2 font-semibold text-sm ${jdInputType === 'file' ? 'border-[#FFD600] text-[#FFD600]' : 'border-transparent text-gray-500 hover:text-gray-300 hover;border-gray-600'}`}>Upload File</button>
                </nav>
              </div>
              {jdInputType === 'text' ? (
                <TextArea label="Job Description Text" placeholder="Paste the job description here..." value={jobDescriptionText} onChange={e => setJobDescriptionText(e.target.value)} />
              ) : (
                <div>
                  <label className="text-sm font-medium text-gray-400 block mb-1">Job Description File</label>
                  <input type="file" onChange={(e) => setJobDescriptionFile(e.target.files[0])} accept=".pdf,.doc,.docx" className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gray-800 file:text-gray-200 hover:file:bg-gray-700" />
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-gray-400 block mb-2">Seniority</label>
                <div className="flex space-x-2">
                  {['Junior', 'Mid', 'Senior'].map(level => (
                    <button key={level} onClick={() => setBasicInfo({ ...basicInfo, seniority: level })} className={`flex-1 py-2 text-sm font-semibold rounded-lg border ${basicInfo.seniority === level ? 'bg-[#FFD600] text-[#0E0E0E] border-[#FFD600]' : 'bg-gray-800 text-gray-300 border-gray-600 hover:bg-gray-700'}`}>
                      {level}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 bg-gray-900/50 text-right rounded-b-xl">
          <button onClick={handleStartClick} disabled={isStarting} className="bg-[#FFD600] text-[#0E0E0E] font-semibold py-2 px-6 rounded-lg hover:bg-[#FFD600]/90 transition-colors flex items-center justify-center space-x-2 disabled:bg-gray-600">
            {isStarting ? <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a 8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg> : <Video size={18} />}
            <span>{isStarting ? 'Starting...' : 'Start Simulation'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const ViewSessionDetailModal = ({ session, onClose }) => {
  const [activeTab, setActiveTab] = useState('review');
  if (!session) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50">
      <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-xl max-w-4xl w-full flex flex-col max-h-[90vh]">
        <div className="p-6 border-b border-gray-700 flex-shrink-0">
          <h3 className="text-xl font-bold text-white">{session.title}</h3>
          <p className="text-sm text-gray-400">Session from {session.createdAt ? new Date(session.createdAt.seconds * 1000).toLocaleString() : 'N/A'}</p>
        </div>

        <div className="border-b border-gray-700 px-6">
          <nav className="-mb-px flex space-x-6">
            <button onClick={() => setActiveTab('review')} className={`py-3 px-1 border-b-2 font-semibold text-sm ${activeTab === 'review' ? 'border-green-500 text-green-400' : 'border-transparent text-gray-500 hover:text-gray-300 hover;border-gray-600'}`}>Session Review</button>
            <button onClick={() => setActiveTab('qna')} className={`py-3 px-1 border-b-2 font-semibold text-sm ${activeTab === 'qna' ? 'border-green-500 text-green-400' : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-gray-600'}`}>AI Q&A</button>
          </nav>
        </div>

        <div className="p-6 overflow-y-auto">
          {activeTab === 'review' ? (
            <div>
              <h4 className="text-lg font-bold mb-4 text-white">Final Evaluation</h4>
              <div className="prose prose-invert max-w-none whitespace-pre-wrap bg-gray-800 p-4 rounded-lg border border-gray-700 mb-6">
                {session.evaluation || "No evaluation available."}
              </div>
            </div>
          ) : (
            <AIQnAView qnaData={session.qna_list || []} isLoading={false} jobTitle={session.title} />
          )}
        </div>

        <div className="p-4 bg-gray-900/50 text-right rounded-b-xl border-t border-gray-700 flex-shrink-0">
          <button onClick={onClose} className="bg-gray-700 text-white font-semibold py-2 px-4 rounded-lg hover:bg-gray-600">Close</button>
        </div>
      </div>
    </div>
  );
};

const CreateNewInterviewCard = ({ onClick }) => (
  <button onClick={onClick} className="aspect-[3/4] border-2 border-dashed border-gray-700 rounded-xl flex flex-col items-center justify-center text-gray-500 hover:bg-gray-800/50 hover:border-green-500 hover:text-green-400 transition-colors">
    <Video size={32} />
    <span className="mt-2 font-semibold">New Simulation</span>
  </button>
);

const InterviewHistoryCard = ({ session, onView, onDelete }) => {
  const previewQuestions = useMemo(() => {
    if (!session.qna_list) return [];
    if (session.qna_list.length > 0 && session.qna_list[0].category) {
      // Flatten sections to get first few questions
      return session.qna_list.flatMap(s => s.questions || []);
    }
    return session.qna_list;
  }, [session.qna_list]);

  return (
    <div onClick={onView} className="group relative aspect-[3/4] bg-[#3C0F6C] rounded-xl shadow-sm border border-gray-700 p-4 flex flex-col justify-between hover:shadow-lg hover:border-green-500/50 transition-shadow cursor-pointer">
      <div>
        <h3 className="font-bold text-white line-clamp-2">{session.title}</h3>
        {previewQuestions.length > 0 ? (
          <ul className="mt-2 text-sm text-gray-400 list-disc list-inside space-y-1">
            {previewQuestions.slice(0, 2).map((item, index) => (
              <li key={index} className="truncate">{item.question}</li>
            ))}
          </ul>
        ) : <p className="text-sm text-gray-500 mt-2">No summary available.</p>}
      </div>
      <div className="text-xs text-gray-500 mt-4">
        {session.createdAt ? new Date(session.createdAt.seconds * 1000).toLocaleDateString() : ''}
      </div>
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="p-1.5 rounded-full hover:bg-red-500/20 text-gray-400 hover:text-red-400">
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
};

const UpgradeModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50">
      <div className="bg-gray-900 border border-yellow-500/30 rounded-xl shadow-xl max-w-md w-full text-center p-8">
        <div className="w-16 h-16 bg-yellow-500/10 border-4 border-yellow-500/20 rounded-full flex items-center justify-center mx-auto">
          <Star className="text-yellow-400" size={32} />
        </div>
        <h3 className="text-2xl font-bold text-white mt-4">Upgrade to Premium</h3>
        <p className="text-gray-400 mt-2">This is a premium feature. Upgrade your account to unlock advanced AI analysis and personalized career counseling.</p>
        <div className="mt-6 flex gap-4">
          <button onClick={onClose} className="w-full bg-gray-700 text-white font-semibold py-2.5 rounded-lg hover:bg-gray-600 transition-colors">Maybe Later</button>
          <button
  onClick={() => window.location.href = "/dashboard/pricing"}
  className="w-full bg-green-500 text-gray-900 font-semibold py-2.5 rounded-lg hover:bg-green-600 transition-colors"
>
  Upgrade Now
</button>

        </div>
      </div>
    </div>
  );
};

export default InterviewPrep;
